import { FormattedHumanReadableRule } from '@/components/rules/components/FormattedHumanReadableRule';
import { AIAutomationDialog } from '@/components/rules/dialogs/AIAutomationDialog';
import { useHumanReadableGenerator } from '@/hooks/use-human-readable-generator';
import CategoryBadge from '@/components/shared/CategoryBadge';
import { CategoryPicker } from '@/components/categories/CategoryPicker';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import useCategories from '@/hooks/use-categories';
import { useInfiniteTransactions } from '@/hooks/use-infinite-transactions';
import useCurrentReport from '@/components/reports/useCurrentReport';
import useRules from '@/hooks/use-rules';
import useTransaction from '@/hooks/use-transaction';
import { AIRuleResponse } from '@polybank/shared/types/ai-contracts';
import { useMutation } from '@tanstack/react-query';
import { FileEdit, Loader2, Sparkles, Tag, Wand2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useBackBlocker } from '@/hooks/use-back-blocker';
import { AppCategory } from '@polybank/shared';
import { rawRulesUtils } from '@polybank/shared/rule-utils';
import appServices from '@/services/appServices';

export type EditMode = 'this-transaction' | 'all-similar';

interface CategoryEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactionId: string;
  currentCategoryId?: string;
  onCategoryUpdated: (
    newCategoryId: string | undefined,
    editMode: EditMode,
    appliedRuleId?: string
  ) => void;
  onCreateRule?: () => void;
}

export function CategoryEditModal({
  isOpen,
  onClose,
  transactionId,
  currentCategoryId,
  onCategoryUpdated,
}: CategoryEditModalProps) {
  const { t } = useTranslation();
  const { categoriesMap } = useCategories();
  const { generate: generateHumanReadable } = useHumanReadableGenerator();

  const { transaction: tx } = useTransaction(transactionId);
  const { updateItem, refetch: refetchTransactions } =
    useInfiniteTransactions();
  const { invalidate: invalidateReport } = useCurrentReport();

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>();
  const selectedCategory = categoriesMap[selectedCategoryId!];
  const [showAiDialog, setShowAiDialog] = useState(false);
  // Back protection with confirmation only when a category has been selected
  useBackBlocker({
    enabled: isOpen && !!selectedCategoryId,
    onBack: () => {
      onClose();
    },
    askConfirm: true,
    confirmTitle: t('categoryEdit.confirmLeaveTitle'),
    confirmDescription: t('categoryEdit.confirmLeaveDescription'),
  });

  // Close modal without confirmation when no category is selected
  useBackBlocker({
    enabled: isOpen && !selectedCategoryId,
    onBack: () => {
      onClose();
    },
  });

  const [editMode, setEditMode] = useState<EditMode>('this-transaction');
  const handleCategoryChange = (category?: AppCategory) => {
    setSelectedCategoryId(category?.$id);
  };

  const appliedRuleId = tx?.applied_rules?.[0];
  const { rulesMap, executeRule, updateItem: updateRule } = useRules();
  const appliedRule = appliedRuleId ? rulesMap[appliedRuleId] : undefined;

  const mutation = useMutation({
    mutationFn: async () => {
      if (editMode === 'this-transaction') {
        await appServices.transactions.updateTransactionCategory(
          transactionId,
          selectedCategoryId
        );
      } else if (editMode === 'all-similar' && appliedRuleId) {
        /**
         * If "all similar" options is selected:
         * 1. Get the current raw rule
         * 2. Change the applied category to the new one
         * 3. Regenerate the human readable
         * 4. Update the rule
         * 5. Execute the rule
         */
        let rawRule = appliedRule?.raw
          ? (JSON.parse(appliedRule.raw) as AIRuleResponse)
          : undefined;

        if (rawRule) {
          rawRule = rawRulesUtils.changeAppliedCategory(
            rawRule,
            selectedCategory.$id!
          );

          // Generate humanReadable client-side
          const newHumanReadable = generateHumanReadable(rawRule!.conditions);
          rawRule!.humanReadable = newHumanReadable;

          await appServices.transactions.updateRuleRaw(appliedRuleId, {
            raw: JSON.stringify(rawRule),
            humanReadable: newHumanReadable,
          });
          updateRule({
            $id: appliedRuleId,
            raw: JSON.stringify(rawRule),
            humanReadable: newHumanReadable,
          });

          await executeRule({ ruleId: appliedRuleId, forceApply: false });
        } else {
          toast.error(t('categoryEdit.updateError'));
        }
      }
    },
    onSuccess: () => {
      toast.success(t('categoryEdit.updateSuccess'));
      updateItem({ $id: transactionId, category_id: selectedCategoryId });
      onCategoryUpdated?.(selectedCategoryId, editMode, appliedRuleId);
      refetchTransactions();
      invalidateReport();
      onClose();
    },
    onError: () => {
      toast.error(t('categoryEdit.updateError'));
    },
  });
  const handleSave = async () => {
    return mutation.mutateAsync();
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        // className='max-w-[90vh]'
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Tag className='h-5 w-5' />
            {t('categoryEdit.title')}
          </DialogTitle>
        </DialogHeader>

        <div className='py-4'>
          {/* Current Category */}
          <div className='mt-4'>
            <Label className='mb-2 block'>
              {t('categoryEdit.currentCategory')}
            </Label>
            <CategoryBadge categoryId={currentCategoryId!} />
            {!!appliedRule?.humanReadable && (
              <FormattedHumanReadableRule
                rawText={appliedRule?.humanReadable}
              />
            )}
          </div>

          {/* Category Picker */}
          <div className='mt-4'>
            <Label className='mb-2 block'>
              {t('categoryEdit.newCategory')}
            </Label>
            <CategoryPicker
              currentCategoryId={currentCategoryId}
              onCategoryChange={handleCategoryChange}
              disabled={mutation.isPending}
            />
          </div>

          {/* Edit Mode Selection */}
          <RadioGroup
            value={editMode}
            onValueChange={(value) => setEditMode(value as EditMode)}
            className='mt-6 gap-3'
          >
            {/* Option 1: Edit only this transaction */}
            <label
              htmlFor='this-transaction'
              className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                editMode === 'this-transaction'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground/50'
              }`}
            >
              <RadioGroupItem
                value='this-transaction'
                id='this-transaction'
                className='mt-0.5'
              />
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-2'>
                  <FileEdit className='h-4 w-4 text-muted-foreground' />
                  <span className='font-medium'>
                    {t('categoryEdit.thisTransactionOnly')}
                  </span>
                </div>
                <p className='text-sm text-muted-foreground mt-1'>
                  {t('categoryEdit.thisTransactionOnlyDescription')}
                </p>
              </div>
            </label>

            {/* Option 2: Edit all similar transactions */}
            <label
              htmlFor='all-similar'
              className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                editMode === 'all-similar'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground/50'
              }`}
            >
              <RadioGroupItem
                value='all-similar'
                id='all-similar'
                className='mt-0.5'
              />
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-2'>
                  {appliedRule ? (
                    <Wand2 className='h-4 w-4 text-muted-foreground' />
                  ) : (
                    <Sparkles className='h-4 w-4 text-purple-500' />
                  )}
                  <span className='font-medium'>
                    {t('categoryEdit.allSimilarTransactions')}
                  </span>
                </div>
                <p className='text-sm text-muted-foreground mt-1'>
                  {appliedRule
                    ? t('categoryEdit.updateRuleDescription')
                    : t('categoryEdit.createRuleDescription')}
                </p>
              </div>
            </label>
          </RadioGroup>
        </div>

        {!selectedCategoryId && (
          <p className='text-sm text-red-400 text-center mb-2'>
            {t('categoryEdit.selectCategoryToContinue')}
          </p>
        )}

        <DialogFooter>
          <Button
            variant='outline'
            onClick={handleCancel}
            disabled={mutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          {editMode === 'all-similar' && !appliedRule ? (
            <Button
              onClick={() => setShowAiDialog(true)}
              disabled={!selectedCategoryId}
              className='gap-2'
            >
              <Sparkles className='w-4 h-4' />
              {t('categoryEdit.createRule')}
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              disabled={mutation.isPending || !selectedCategoryId}
            >
              {mutation.isPending ? (
                <Loader2 className='w-4 h-4 animate-spin' />
              ) : null}
              {mutation.isPending
                ? t('categoryEdit.saving')
                : editMode === 'this-transaction'
                  ? t('categoryEdit.saveCategory')
                  : t('categoryEdit.updateRule')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* AI Automation Dialog for creating new rules */}
      {tx && (
        <AIAutomationDialog
          open={showAiDialog}
          onOpenChange={(open) => {
            setShowAiDialog(open);
            if (!open) {
              // Close the category edit modal when AI dialog is closed
              onClose();
            }
          }}
          transactionDescription={tx.description || ''}
          transactionAmount={Math.abs(tx.amount || 0)}
          transactionCategory={currentCategoryId}
          transactionId={transactionId}
          forceApply={false}
          targetCategoryName={selectedCategory?.name}
        />
      )}
    </Dialog>
  );
}
